import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AttributeGroupResponse, useCreateAttributeGroup, useUpdateAttributeGroup } from '@/entities/attributes';
import { useHasPermission } from '@/entities/profile';
import { useFormCodeGeneration } from '@/shared/hooks';
import { useAppNavigate } from '@/shared/lib';
import { AttributeGroupFormValues } from './types';
import { useAttributeGroupSchema } from './validation';
import { useFormGuardActions } from '@/entities/formGuard';

type Params = {
  initial?: AttributeGroupResponse;
  onSuccess?: () => void;
};

export const useAttributeGroupForm = (params: Params) => {
  const { initial, onSuccess } = params;
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_ATTRIBUTE_GROUP');
  const schema = useAttributeGroupSchema();
  const { resetFormGuard } = useFormGuardActions();

  const isEdit = allowAdmin && !!initial;
  const isDisabled = !allowAdmin || initial?.code === 'system';

  const defaults: AttributeGroupFormValues = useMemo(
    () => ({
      name: initial?.name ?? '',
      code: initial?.code ?? '',
      description: initial?.description ?? '',
      attributes: initial?.attributes?.map((a) => ({ code: a.code })) ?? [],
    }),
    [initial],
  );

  const form = useForm<AttributeGroupFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });

  // Use code generation hook
  const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
    getName: () => form.getValues('name'),
    getCode: () => form.getValues('code'),
    setCode: (code) => form.setValue('code', code),
    entityType: 'attributeGroup',
    isEdit,
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  const { mutateAsync: createGroup, isPending: isCreating } = useCreateAttributeGroup();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateAttributeGroup();
  const isLoading = isCreating || isUpdating;

  const onSubmit = form.handleSubmit(async (values) => {
    if (isEdit) {
      if (initial?.code === 'system') {
        return;
      }
      await updateGroup({
        code: initial!.code,
        body: {
          name: values.name,
          description: values.description,
          attributes: values.attributes,
        },
      });
      // Отправленное становится сохранённым: карточка перестаёт считать себя изменённой
      form.reset(values);
    } else {
      const created = await createGroup({
        code: values.code,
        name: values.name,
        description: values.description,
        attributes: values.attributes,
      });
      form.reset(values);
      resetFormGuard();
      navigate('/attribute-groups/:id', { id: created.code }, { replace: true });
    }

    resetFormGuard();
    onSuccess?.();
  });

  const handleTitleSave = async (value: string) => {
    form.setValue('name', value);

    if (isEdit && value !== initial?.name && initial?.code !== 'system') {
      await updateGroup({
        code: initial!.code,
        body: {
          name: value,
          description: initial!.description,
        },
      });
    }
  };

  return {
    form,
    isEdit,
    isLoading,
    isDisabled,
    onSubmit,
    handleTitleSave,
    handleNameBlur,
    isCodeLocked,
    isGeneratingCode,
  } as const;
};
